import {Component, OnDestroy, OnInit} from '@angular/core';
import {loadScript} from '@paypal/paypal-js';
import {ApiService} from '../../../api/service/api.service';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {BaseFormGroup} from '../../shared/utils/base-form-group';
import {ConfigService, NotificationService} from '../../../core/services';
import {ActivatedRoute, Router} from '@angular/router';
import {Ticket} from '../../../api/model/Ticket.model';
import {Invoice} from '../../../api/model/Invoice.model';
import {FormHelpers} from '../../shared/utils/form-helpers';
import {DropdownModel} from '../../../api/model/shared/dropdown.model';
import {TicketPartnerStatus} from '../../../api/status/ticket-partner.status';
import {forkJoin} from 'rxjs';
import {InvoiceService} from '../../invoice/service/invoice.service';
import {PaymentFlowService} from '../payment-flow.service';
import {PaymentConfigModel} from '../../../api/model/Payment.config.model';
import {PaymentStep} from '../models/payment-step';
import {PaymentItemType} from '../models/payment-type';

@Component({
  selector: 'tj-payment-details',
  templateUrl: './payment-details.component.html',
  styleUrls: ['./payment-details.component.scss'],
})
export class PaymentDetailsComponent extends BaseFormGroup implements OnInit, OnDestroy {
  form: UntypedFormGroup;

  readonly PAYMENT_BY_CARD = 0;

  readonly PAYMENT_BY_VAULT = 1;

  isSubmitting = false;

  paypal;

  cardFields;

  vaultId;

  paymentStep: PaymentStep = PaymentStep.INITIAL;

  paymentSteps: typeof PaymentStep = PaymentStep;

  paymentMethodIndex = this.PAYMENT_BY_CARD;

  ticket: Ticket | null = null;

  invoice: Invoice | null = null;

  orderData = null;

  lastError = null;

  paymentConfig: PaymentConfigModel = new PaymentConfigModel(0, 0);

  paymentDetails = {
    netAmount: 0,
    applyTax: false,
    taxRate: 0,
    taxFee: 0,
    applyCCFee: false,
    total: 0,
    ccFeeRate: 0,
    ccFee: 0,
  };

  person = {
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  };

  formChangesSubscription;

  itemType: PaymentItemType = null;

  items: Ticket[] | Invoice[];

  itemId: number[];

  isSandbox = true;

  currentStep = 0;

  itemTypes: DropdownModel[] = [
    {label: 'Ticket', value: PaymentItemType.TICKET},
    {label: 'Invoice', value: PaymentItemType.INVOICE},
  ];

  private cardFieldsInitialized = false; // Track credit card fields initialization state

  steps = [{label: 'Payment Details'}, {label: 'Payment Method'}, {label: 'Payment Status'}];

  constructor(
    private api: ApiService,
    private configService: ConfigService,
    private router: Router,
    private route: ActivatedRoute,
    private invoiceService: InvoiceService,
    private notificationService: NotificationService,
    private fb: UntypedFormBuilder,
    private paymentFlowService: PaymentFlowService,
  ) {
    super();
  }

  ngOnInit() {
    this.paymentConfig = this.configService.getPaymentConfig();
    this.initParams();
    this.initForm();
    this.initPaypal();
  }

  initParams() {
    this.route.queryParams.subscribe(params => {
      if (params.itemType === 'invoice') {
        this.itemType = PaymentItemType.INVOICE;
      }
      if (params.itemType === 'ticket') {
        this.itemType = PaymentItemType.TICKET;
      }
      this.itemId = params.itemId.split(',').map(it => +it);
      this.loadItem();
    });
  }

  initForm() {
    this.form = new UntypedFormGroup({});
    const formControls = {
      itemType: this.fb.control(this.itemType),
      items: this.fb.control([]),
      netAmount: this.fb.control(0, [
        Validators.required,
        Validators.min(0.01),
        Validators.pattern(/^\d*\.?\d*$/),
      ]),
      applyTax: this.fb.control(false),
      taxRate: this.fb.control(this.paymentConfig.taxRate),
      applyCCFee: this.fb.control(false),
      ccFeeRate: this.fb.control(this.paymentConfig.creditCardFee),
      email: this.fb.control(''),
      description: this.fb.control(''),
      invoiceId: this.fb.control(''),
      saveCard: this.fb.control(true),
      customerName: this.fb.control(''),
      address: this.fb.control(''),
    };

    this.buildForm(this.form, formControls, this.group);
    this.defineGetters(formControls);

    // Initialize paymentDetails with initial form values
    const initialValues = this.form.getRawValue();
    this.paymentDetails.taxRate = initialValues.taxRate;
    this.paymentDetails.ccFeeRate = initialValues.ccFeeRate;

    this.formChangesSubscription = this.form.valueChanges.subscribe(c => {
      const pd = this.paymentDetails;

      pd.taxRate = c.taxRate;
      pd.ccFeeRate = c.ccFeeRate;

      pd.applyTax = c.applyTax;
      pd.applyCCFee = c.applyCCFee;

      pd.netAmount = parseFloat(c.netAmount);
      pd.taxFee = pd.applyTax ? (pd.netAmount * pd.taxRate) / 100 : 0;
      pd.ccFee = pd.applyCCFee ? (pd.netAmount * pd.ccFeeRate) / 100 : 0;
      pd.total = pd.netAmount + pd.taxFee + pd.ccFee;
    });
  }

  onSubmit() {
    if (this.paymentMethodIndex === this.PAYMENT_BY_CARD) {
      this.onSubmitByCard();
    } else {
      this.onSubmitByVaultId();
    }
  }

  onSubmitByCard() {
    const netAmount = this.form.get('netAmount').getRawValue() as number;
    if (netAmount <= 0) {
      this.notificationService.error('Net amount should be greater than 0');
    } else if (this.form.valid && this.cardFields) {
      this.isSubmitting = true;
      this.cardFields.submit();
    } else {
      this.isSubmitting = false;
      FormHelpers.validateAllFormFields(this.form);
    }
  }

  onPayByStripe() {
    const order = this.prepareOrder();
    this.paymentFlowService.setOrder(order);
    this.router.navigate(['/payment/stripe'], {
      queryParams: {
        itemId: this.itemId,
        itemType: this.itemType,
        paymentVendor: 'STRIPE',
      },
    });
  }

  onSubmitByVaultId() {
    const netAmount = this.form.get('netAmount').getRawValue() as number;
    if (netAmount <= 0) {
      this.notificationService.error('Net amount should be greater than 0');
    } else if (!this.vaultId || !this.vaultId.trim()) {
      this.notificationService.error('Vault id is not specified');
    } else if (this.form.valid) {
      const vaultId = this.vaultId;
      const order = this.prepareOrder();

      this.isSubmitting = true;
      return this.api.payment
        .createPaypalOrder(
          {
            ...order,
            vaultId,
          },
          this.isSandbox,
        )
        .toPromise()
        .then(orderData => {
          this.orderData = orderData;
          this.paymentStep = PaymentStep.COMPLETED;
          this.currentStep = 2;
          return orderData.id as string;
        })
        .catch(error => {
          this.paymentStep = PaymentStep.FAILED;
          this.lastError = error;
          this.isSubmitting = false;
          console.error('Something went wrong:', error);
        });
    } else {
      this.isSubmitting = false;
      FormHelpers.validateAllFormFields(this.form);
    }
  }

  ngOnDestroy() {
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }

    // Clean up PayPal instance
    if (this.paypal) {
      this.paypal = null;
    }

    // Clean up card fields
    if (this.cardFields) {
      this.cardFields = null;
    }
  }

  getControl(fcName: string): UntypedFormControl {
    return this.currentFormGroup.get(fcName) as UntypedFormControl;
  }

  private initPaypal() {
    // Wait for next tick to ensure DOM is ready
    setTimeout(() => {
      this.isSandbox = this.configService.isSandbox();
      const paypalClientId = this.configService.getPaypalClientId(this.isSandbox);
      loadScript({
        clientId: paypalClientId,
        vault: true,
        components: 'buttons,marks,card-fields',
        intent: 'capture',
        currency: 'USD',
      })
        .then(paypal => {
          this.paypal = paypal;
        })
        .catch(error => {
          console.error('Failed to load PayPal SDK:', error);
          this.notificationService.error('Failed to initialize payment system');
          this.paymentStatus.emit({step: PaymentStep.FAILED, error});
        });
    }, 0);
  }

  private prepareOrder() {
    const data = this.form.getRawValue();
    const order = {
      order: [
        {
          itemType: this.itemType,
          items: this.itemId.map(id => id),
          description: data.description,
          customerName: data.customerName,
          address: data.address,
          netAmount: this.paymentDetails.netAmount,
          total: this.paymentDetails.total,
          applyTax: this.paymentDetails.applyTax,
          applyCCFee: this.paymentDetails.applyCCFee,
          taxRate: this.paymentDetails.taxRate,
          taxFee: this.paymentDetails.taxFee,
          ccFeeRate: this.paymentDetails.ccFeeRate,
          ccFee: this.paymentDetails.ccFee,
        },
      ],
      email: data.email,
      savePaymentMethod: data.saveCard,
    };
    console.log('prepareOrder - final order:', order);
    return order;
  }

  private loadItem() {
    if (this.itemType === PaymentItemType.TICKET) {
      this.loadTicket(this.itemId[0]);
    } else if (this.itemType === PaymentItemType.INVOICE) {
      this.loadInvoices(this.itemId);
    }
  }

  private loadTicket(ticketId: number) {
    this.api.ticket.findOne(ticketId).subscribe(ticket => {
      this.items = [ticket];
      this.form.get('customerName').setValue(ticket.customer.name);
      this.form.get('address').setValue(ticket.address.addressFormatted);
      this.form.get('invoiceId').setValue(ticket.id);

      this.setEmail(ticket);

      if (ticket.invoiceAmount > 0) {
        this.form.get('netAmount').setValue(ticket.invoiceAmount);
      } else if (ticket.technicians.length > 0) {
        let sum = 0;

        ticket.technicians.forEach(technician => {
          const bill = technician.bill || 0;
          const creditCard = technician.creditCard || 0;
          sum += bill + creditCard;
        });

        this.form.get('netAmount').setValue(sum);
      }

      this.setPaymentDetails(ticket);
    });
  }

  private loadInvoices(invoicesIds: number[]) {
    const invoiceRequests = invoicesIds.map(id => this.api.invoice.getInvoice(id));
    forkJoin(invoiceRequests).forEach((invoices: Invoice[]) => {
      const eligible = this.invoiceService.checkEligibilityForPayment(invoices);

      const nonPaidInvoices = invoices.filter(it => it.status !== 'PAID');

      if (nonPaidInvoices.length === 0) {
        this.notificationService.error('Invoices already paid');
      } else if (eligible) {
        this.notificationService.error('Selections contains invoices of different accounts');
      } else {
        this.items = nonPaidInvoices;
        const sum = nonPaidInvoices.reduce((partialSum, a) => partialSum + a.totalAmount, 0);
        this.api.ticket.findOne(nonPaidInvoices[0].items[0].ticketId).subscribe(ticket => {
          this.form.get('netAmount').setValue(sum);
          this.form.get('invoiceId').setValue(this.itemId);
          this.setEmail(ticket);
          this.setPaymentDetails(ticket);
        });
      }
    });
  }

  private setEmail(ticket: Ticket) {
    if (ticket.ticketPartnerStatus !== TicketPartnerStatus.PRIVATE) {
      if (ticket.mainPartnerContactPerson && ticket.mainPartnerContactPerson.contacts.length > 0) {
        const accountEmailContact = ticket.mainPartnerContactPerson.contacts.find(
          c => c.type === 'EMAIL',
        );
        if (accountEmailContact) {
          this.form.get('email').setValue(accountEmailContact.value);
        }
      }
    } else {
      if (
        ticket.mainCustomerContactPerson &&
        ticket.mainCustomerContactPerson.contacts.length > 0
      ) {
        const customerEmailContact = ticket.mainCustomerContactPerson.contacts.find(
          c => c.type === 'EMAIL',
        );
        if (customerEmailContact) {
          this.form.get('email').setValue(customerEmailContact.value);
        }
      }
    }
  }

  private setPaymentDetails(ticket: Ticket) {
    if (ticket.ticketPartnerStatus === TicketPartnerStatus.REGISTERED) {
      this.paymentMethodIndex = this.PAYMENT_BY_VAULT;
      this.vaultId = ticket.partner?.payment?.transactionId;
    }

    const ccFeeApplyConfig = this.configService.getPrivateAccountDefaultCCFeeApplyConfig();
    if (ticket.ticketPartnerStatus !== TicketPartnerStatus.REGISTERED && ccFeeApplyConfig) {
      this.form.get('applyCCFee').setValue(true);
    }
  }

  backToPayment() {
    this.currentStep = 0;
  }

  initPayByCreditCard() {
    this.paymentMethodIndex = this.PAYMENT_BY_CARD;
  }

  initVaultId() {
    this.paymentMethodIndex = this.PAYMENT_BY_VAULT;
  }

  back() {
    window.history.back();
  }

  onStepBack() {
    // Clean up PayPal instance before going back
    if (this.paypal) {
      this.paypal = null;
    }
    if (this.cardFields) {
      this.cardFields = null;
    }
    this.currentStep--;
  }

  onFormValid(data: {form: UntypedFormGroup; paymentDetails: any}) {
    this.form = data.form;
    this.paymentDetails = data.paymentDetails;
    // Ensure items are set in the form
    this.form.patchValue({
      items: this.itemId.map(id => id),
    });
  }

  onNextStep() {
    // Reinitialize PayPal when moving to next step
    this.initPaypal();
    this.currentStep = 1;
    // Set the items in the form
    this.form.patchValue({
      items: this.itemId.map(id => id),
    });
  }

  onPaymentStatus(event: {step: PaymentStep; data?: any; error?: any}) {
    this.paymentStep = event.step;
    if (event.step === PaymentStep.COMPLETED) {
      this.orderData = event.data;
      this.currentStep = 2;
    } else if (event.step === PaymentStep.FAILED) {
      this.lastError = event.error;
      this.currentStep = 2;
    }
  }
}
